<template>
    <app-layout v-if="isPageReady" class="peraperic">
        <common-content-spacer height="medium">
            <common-content-spacer height="small">
                <common-breadcrumbs v-if="!showConfirm" :breadcrumbs="pageBreadcrumbs"></common-breadcrumbs>

                <common-page-header
                    v-if="!showConfirm"
                    :heading="`${pageHeading} ${usersWithinNetwork?.userIds?.length ? pageHeadingNetwork : ''}`"
                ></common-page-header>

                <switch-input
                    v-if="!isReferralFormShown && !showConfirm"
                    :label="$t('pages.referrals.new.steps.results.view_map')"
                    :value="showMap"
                    :isLabelOnLeft="true"
                    @change="showMap = !showMap"
                ></switch-input>
            </common-content-spacer>

            <radio-input
                v-if="!isReferralFormShown && !showConfirm"
                :value="selectionType"
                :options="selectionTypeOptions"
                direction="column"
                checkedIcon="radio-check"
                @change="changeSelectionType($event)"
            ></radio-input>

            <div v-if="!isReferralFormShown && !showConfirm" class="results" :class="{ 'results-map': showMap }">
                <common-content-spacer class="results-container" height="medium">
                    <common-content-spacer
                        v-if="results.length || physicianRecords.length"
                        height="medium"
                        class="results-container--grid"
                        :class="{ 'results-container--grid__active': selectedPracticeId }"
                    >
                        <common-grid v-if="$route.query.practiceType === PracticeTypeEnum.PHYSICIAN">
                            <common-grid-column
                                v-for="(result, index) in results"
                                :id="result?.practice?.id"
                                :key="index"
                                :class="{
                                    'results-container--grid__active--practice': selectedPracticeId == result?.id,
                                }"
                            >
                                <practice-member-card
                                    :id="result.id"
                                    :physician="result"
                                    :checkedValues="checkedValues"
                                    :showCardAvailability="false"
                                    :isSelectionAllowed="selectionType === 'CUSTOM'"
                                    @checkAvailability="checkAvailability($event)"
                                    @selection-checked="selectionChecked"
                                    @selection-changed="selectionChanged"
                                ></practice-member-card>
                            </common-grid-column>

                            <common-grid-column
                                v-if="showPhysicianRecordsTitle"
                                :key="results.length"
                                class="results--physician-records-title"
                            >
                                {{ $t('pages.referrals.new.steps.results.physicians_not_in_medmatch') }}
                            </common-grid-column>

                            <common-grid-column
                                v-for="(result, index) in physicianRecords"
                                :id="result?.practice?.id"
                                :key="results.length + index + 1"
                            >
                                <practice-member-card
                                    :physician="{ isRecord: true, ...result }"
                                    :isUserProfileLinkEnabled="false"
                                    :showNetworkInclusionStatus="false"
                                    :showCardAvailability="false"
                                    :isSelectionAllowed="selectionType === 'CUSTOM'"
                                ></practice-member-card>
                            </common-grid-column>
                        </common-grid>

                        <common-grid v-if="$route.query.practiceType === PracticeTypeEnum.ANCILLARY">
                            <common-grid-column
                                v-for="(result, index) in results"
                                :id="result?.id"
                                :key="index"
                                :class="{
                                    'results-container--grid__active--practice': selectedPracticeId == result?.id,
                                }"
                            >
                                <ancillary-member-card
                                    :ancillary="result"
                                    :checkedValues="checkedValues"
                                    :showCardAvailability="false"
                                    :isSelectionAllowed="selectionType === 'CUSTOM'"
                                    @selection-checked="selectionChecked"
                                    @selection-changed="selectionChanged"
                                ></ancillary-member-card>
                            </common-grid-column>
                        </common-grid>
                    </common-content-spacer>
                    <common-buttons v-if="results.length || physicianRecords.length">
                        <common-button
                            v-if="showMoreButton"
                            :label="$t('pages.referrals.index.buttons.show_more')"
                            :isProcessing="isProcessing"
                            type="primary"
                            size="large"
                            @click="showMore()"
                        ></common-button>
                    </common-buttons>

                    <common-blank-slate v-else>
                        {{ $t('pages.referrals.new.steps.results.blank_slate.message') }}
                        <router-link :to="{ name: 'referrals.new' }">
                            {{ $t('pages.referrals.new.steps.results.blank_slate.link') }}
                        </router-link>
                    </common-blank-slate>
                </common-content-spacer>
                <div v-if="showMap" class="map-container">
                    <map-component
                        :markers="markers"
                        @popup-open="onPopupOpen"
                        @popup-close="selectedPracticeId = null"
                        @distance-changed="distanceChanged"
                    ></map-component>
                </div>
            </div>

            <div v-if="checkedValues.length && !isReferralFormShown && !showConfirm" class="results__sticky">
                <common-buttons class="results__sticky-buttons">
                    <common-button
                        :label="$t(`pages.referrals-pool.index.create.referrals_pool`)"
                        size="large"
                        type="primary"
                        @click="toggleReferralCreationForm"
                    ></common-button>
                </common-buttons>
            </div>
            <common-content-wrapper v-if="isReferralFormShown" width="medium">
                <referrals-new-make-form
                    :checkedValues="checkedValues"
                    :submitLabel="'forms.referrals_pool.make.buttons.send'"
                    @startOver="toggleReferralCreationForm"
                    @sent="handleReferralsPoolSubmition"
                ></referrals-new-make-form>
            </common-content-wrapper>
            <referrals-confirmation
                v-if="showConfirm"
                :heading="$t('pages.referrals.new.steps.confirmation.heading')"
                type="success"
            >
                <template #content>
                    <common-summary>
                        <common-summary-item :label="$t('pages.referrals.new.steps.confirmation.summary.patient_name')">
                            <common-user-name :user="referralInfo?.patient"></common-user-name>
                        </common-summary-item>

                        <common-summary-item :label="$t('pages.referrals.new.steps.confirmation.summary.diagnosis')">
                            {{ referralInfo.diagnosis }}
                        </common-summary-item>
                    </common-summary>
                </template>

                <template #footer>
                    <common-buttons>
                        <common-button
                            :label="$t('pages.auth.signup.steps.congratulations.content.button.proceed_to_dashboard')"
                            size="large"
                            type="primary"
                            @click="$router.push({ name: 'index.home' })"
                        ></common-button>
                    </common-buttons>
                </template>
            </referrals-confirmation>
        </common-content-spacer>
    </app-layout>
</template>

<script>
    import PageWithTitleMixin from 'mixins/PageWithTitleMixin'
    import PageWithElementsMixin from 'mixins/PageWithElementsMixin'
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import ReferralsNewPageMixin from 'mixins/ReferralsNewPageMixin'
    import PhysiciansAPI from 'api/PhysiciansAPI'
    import FacilityServicesAPI from 'api/FacilityServicesAPI'
    import PhysicianRecordsAPI from 'api/PhysicianRecordsAPI'
    import AppLayout from 'components/layouts/AppLayout'
    import PracticeMemberCard from 'components/page-elements/practice/PracticeMemberCard'
    import AncillaryMemberCard from 'components/page-elements/practice/AncillaryMemberCard'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import SwitchInput from 'components/form-inputs/SwitchInput'
    import MapComponent from 'components/page-elements/map/MapComponent'
    import ReferralsNewMakeForm from 'components/forms/ReferralsNewMakeForm'
    import ReferralsPoolAPI from 'api/ReferralsPoolAPI'
    import FormMixin from 'mixins/FormMixin'
    import ReferralsConfirmation from 'components/page-elements/ReferralsConfirmation'
    import RadioInput from 'components/form-inputs/RadioInput'

    export default {
        mixins: [PageWithTitleMixin, PageWithElementsMixin, PageWithDataMixin, ReferralsNewPageMixin, FormMixin],

        components: {
            AppLayout,
            PracticeMemberCard,
            AncillaryMemberCard,
            SwitchInput,
            MapComponent,
            ReferralsNewMakeForm,
            ReferralsConfirmation,
            RadioInput,
        },

        data() {
            return {
                results: [],
                physicianRecords: [],
                isMapOpen: false,
                isProcessing: false,
                offset: 0,
                totalCount: 0,
                physicianRecordsTotalCount: 0,
                PracticeTypeEnum: PracticeTypeEnum,
                showMap: false,
                markers: [],
                selectedPracticeId: null,
                checkedValues: [],
                isReferralFormShown: false,
                showConfirm: false,
                referralInfo: null,
                isAllSelected: false,
                isAllSelectedFromYourNetwork: false,
                selectionType: null,
                totalUsersIds: null,
                usersWithinNetwork: null,
            }
        },

        computed: {
            pageHeading() {
                const practiceType = this.$route.query.practiceType

                return this.$tc(`pages.referrals.new.steps.results.heading.${practiceType}`, this.totalCount, {
                    count: this.totalCount,
                })
            },

            pageHeadingNetwork() {
                return this.$tc(`pages.referrals.new.steps.results.heading.network`, this.usersWithinNetwork?.userIds?.length, {
                    count: this.usersWithinNetwork?.userIds?.length,
                })
            },

            selectionTypeOptions() {
                const options = [
                    {
                        label: `Select all (${this.totalUsersIds?.length || 0})`,
                        value: 'ALL',
                    },

                    {
                        label: 'Custom',
                        value: 'CUSTOM',
                    },
                ]
                if (this.$auth.getUser().physicianGroupId) {
                    const networkOption = {
                        label: `Select All From Your Network (${this.usersWithinNetwork?.userIds?.length || 0})`,
                        value: 'ALL_NETWORK',
                    }

                    options.splice(1, 0, networkOption)
                }
                return options
            },

            showMoreButton() {
                return this.totalCount > this.results.length || this.physicianRecordsTotalCount > this.physicianRecords.length
            },

            showPhysicianRecordsTitle() {
                return this.physicianRecordsTotalCount >= this.physicianRecords.length && this.physicianRecordsTotalCount
            },
        },

        methods: {
            loadMatchesWithDifferentRange(range) {
                this.getMatches({ ...this.$route.query, range, offset: this.offset })
            },

            changeSelectionType(event) {
                this.selectionType = event
                if (this.selectionType === 'ALL') {
                    this.checkedValues = this.totalUsersIds
                }

                if (this.selectionType === 'ALL_NETWORK') {
                    this.checkedValues = this.usersWithinNetwork?.userIds
                }
                if (this.selectionType === 'CUSTOM') {
                    this.checkedValues = []
                }
            },

            checkAvailability(query) {
                const q = { ...this.$route.query, ...query }
                if (!q.subSpecialityId) {
                    const physicianSpecialities = query.target.specialities
                    q.subSpecialityId = physicianSpecialities.find((specObj) => {
                        return Number(specObj.speciality.id) === Number(q.specialityId)
                    }).subSpeciality.id
                }
                this.$router.push({
                    name: 'referrals.new.make',
                    query: q,
                    params: {
                        existingReferral: this.$route.params.existingReferral,
                        originalQuery: this.$route.query,
                    },
                })
            },

            toggleMapView(flag = !this.isMapOpen) {
                this.isMapOpen = flag
            },

            getMatches(data, append) {
                this.isProcessing = true
                if (this.results.length < this.totalCount) {
                    return this.$route.query.practiceType === PracticeTypeEnum.ANCILLARY
                        ? this.fetchAncillaryMatches({ ...this.$route.query, offset: this.offset }, append)
                        : this.fetchMatches({ ...this.$route.query, offset: this.offset }, append)
                }
                if (this.$route.query.practiceType === PracticeTypeEnum.PHYSICIAN) {
                    this.fetchPhysicianRecords(data)
                }
            },

            fetchMatches(data, append) {
                this.isProcessing = true
                PhysiciansAPI.getMatchesList(data).then((results) => {
                    this.isProcessing = false
                    if (append) {
                        this.results = this.results.concat(results.users)
                        this.getLocationsMarkers(this.results)
                        return
                    }
                    this.totalUsersIds = results.totalUsersIds
                    this.usersWithinNetwork = results.usersWithinNetwork
                    this.results = results.users
                    this.getLocationsMarkers(this.results)
                    this.totalCount = Number(results._headers['x-total-count'])

                    if (this.results.length === this.totalCount) {
                        this.offset = 0
                        this.fetchPhysicianRecords({ ...data, offset: this.offset })
                    }
                })
            },

            fetchPhysicianRecords(data) {
                return PhysicianRecordsAPI.getList(data).then((results) => {
                    this.physicianRecords = this.physicianRecords.concat(results)
                    this.physicianRecordsTotalCount = Number(results._headers['x-total-count'])
                    this.isProcessing = false
                })
            },

            fetchAncillaryMatches(data, append) {
                data.testIds = data.testIds.toString()
                if (this.$route.query.practiceType === PracticeTypeEnum.ANCILLARY) {
                    data.testIds = data.testIds.toString()
                }
                FacilityServicesAPI.getMatchesList(data).then((results) => {
                    this.isProcessing = false
                    if (append) {
                        this.results = this.results.concat(results)
                        this.getAncillaryLocationsMarkers(this.results)
                        return
                    }
                    this.results = results
                    this.getAncillaryLocationsMarkers(this.results)
                    this.totalCount = Number(results._headers['x-total-count'])

                    if (this.results.length === this.totalCount) {
                        this.offset = 0
                    }
                })
            },

            distanceChanged(distance) {
                this.$route.query.practiceType === PracticeTypeEnum.ANCILLARY
                    ? this.fetchAncillaryMatches({ ...this.$route.query, distance, offset: this.offset }, false)
                    : this.fetchMatches({ ...this.$route.query, distance, offset: this.offset }, false)
            },

            showMore() {
                this.offset = Number(this.$route.query.limit) + this.offset
                this.getMatches({ ...this.$route.query, offset: this.offset }, true)
            },

            getLocationsMarkers(results) {
                const locations = []
                this.markers = []
                results.forEach((item) => {
                    item.practice.locations.forEach((location) => {
                        const locationWithPracticeName = { ...location, item }
                        locations.push(locationWithPracticeName)
                    })
                })
                this.markers = locations
            },

            getAncillaryLocationsMarkers(results) {
                const locations = []
                this.markers = []
                results.forEach((item) => {
                    item.locations.forEach((location) => {
                        const locationWithPracticeName = { ...location, item }
                        locationWithPracticeName.longitude = location?.coordinates?.longitude
                        locationWithPracticeName.latitude = location?.coordinates?.latitude
                        locations.push(locationWithPracticeName)
                    })
                })
                this.markers = locations
            },

            onPopupOpen(event) {
                this.selectedPracticeId = event
                const el = document.getElementById(this.selectedPracticeId)
                el.scrollIntoView({
                    behavior: 'auto',
                    block: 'center',
                    inline: 'center',
                })
            },

            selectionChecked(id) {
                this.isChecked = this.checkedValues.includes(id)
            },

            selectionChanged(id) {
                if (this.checkedValues.includes(id)) {
                    this.checkedValues.splice(this.checkedValues.indexOf(id), 1)
                } else {
                    this.checkedValues.push(id)
                }
            },

            toggleReferralCreationForm() {
                this.totalCount = this.checkedValues.length
                this.isReferralFormShown = !this.isReferralFormShown
            },

            handleReferralsPoolSubmition(event) {
                delete event.fromUserId
                delete event.toUserId
                this.referralInfo = event

                const data =
                    this.$route.query.practiceType === PracticeTypeEnum.ANCILLARY
                        ? { practiceIds: this.checkedValues, ...event }
                        : { userIds: this.checkedValues, ...event }
                data.searchBy = this.$route.query.searchBy
                data.searchByZip = this.$route.query.zip
                data.searchByAddress = this.$route.query.address
                data.searchByCity = this.$route.query.city
                data.searchByState = this.$route.query.state
                data.timeframe = this.$route.query.searchByTimeWindow

                this.handleRequest(ReferralsPoolAPI.create(data)).then(() => {
                    this.toggleShowConfirm()
                    this.toggleReferralCreationForm()
                })
            },

            toggleShowConfirm() {
                this.showConfirm = !this.showConfirm
            },

            selectAllToggle() {
                this.isAllSelected = !this.isAllSelected
                if (this.isAllSelected) {
                    this.checkedValues = this.results.map((record) => record.id)
                } else {
                    this.checkedValues = []
                }
            },

            selectAllFromYourNetworkToggle() {
                this.isAllSelectedFromYourNetwork = !this.isAllSelectedFromYourNetwork

                if (this.isAllSelectedFromYourNetwork) {
                    this.checkedValues = this.results
                        .filter((record) => record.physicianGroup?.id === 4)
                        .map((record) => record.id)
                } else {
                    this.checkedValues = []
                }
            },
        },

        mounted() {
            this.$route.query.practiceType === PracticeTypeEnum.ANCILLARY
                ? this.fetchAncillaryMatches({ ...this.$route.query, offset: this.offset })
                : this.fetchMatches({ ...this.$route.query, offset: this.offset })
        },
    }
</script>

<style lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';
    .results {
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;
        @include breakpoint(laptop) {
            flex-direction: column-reverse;
        }
        .map-container {
            height: 600px;
        }

        .results-container {
            display: flex;
            flex-wrap: wrap;
            width: 100%;

            &--grid {
                max-height: 60vh;
                width: 100%;
                overflow-y: scroll;
                overflow-x: hidden;
                padding: 10px;

                &__active {
                    .card {
                        opacity: 0.5;
                    }

                    &--practice {
                        .card {
                            opacity: 1;
                        }
                    }
                }
            }

            .buttons {
                width: 100%;
            }
        }

        &__sticky {
            position: fixed;
            width: 82%;
            height: 60px;
            bottom: 0;
            right: 0;
            background-color: white;

            &-buttons {
                margin: 5px;
            }
        }
    }

    .results-map {
        .map-container {
            @include breakpoint(laptop) {
                width: 100%;
                max-height: 600px;
                height: 600px;
                margin-bottom: 70px;
            }

            .map-component {
                @include breakpoint(laptop) {
                    width: 100%;
                    max-height: 600px;
                    height: 600px;
                }
            }
            width: 450px;
        }

        .results-container {
            .grid__column {
                @include breakpoint(tablet) {
                    flex-basis: 100%;
                }
                flex-basis: calc(100% / 2);
            }
            @include breakpoint(laptop) {
                width: 100%;
            }
            width: calc(100% - 550px);
        }
    }
</style>
